import React, { useState } from 'react';
import {
  createHarvestParticipant,
  issueParticipantInvite,
  issueParticipantRecovery,
  searchHarvestParticipants,
} from '../../services/harvestingService';

const box = { border: '1px solid #cbd5e1', borderRadius: 8, padding: 14, marginBottom: 16, background: '#fff' };
const field = { display: 'block', margin: '8px 0', width: '100%', maxWidth: 420, padding: 8 };
const button = { padding: '7px 12px', margin: '4px 6px 4px 0', cursor: 'pointer' };

export default function AssistedParticipantPanel({ tenantId, onSelectionChange }) {
  const [participant, setParticipant] = useState(null);
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [name, setName] = useState('');
  const [cardReference, setCardReference] = useState('');
  const [email, setEmail] = useState('');
  const [eligibilityConfirmed, setEligibilityConfirmed] = useState(false);
  const [inviteConsent, setInviteConsent] = useState(false);
  const [destinationConfirmed, setDestinationConfirmed] = useState(false);
  const [recoveryConfirmed, setRecoveryConfirmed] = useState(false);
  const [handoverLink, setHandoverLink] = useState('');
  const [handoverExpiresAt, setHandoverExpiresAt] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const selectParticipant = (next, preserveConsent = false) => {
    setParticipant(next);
    setHandoverLink('');
    setHandoverExpiresAt('');
    setMessage('');
    setEmail('');
    setInviteConsent(false);
    if (!preserveConsent) setConsentConfirmed(false);
    onSelectionChange(next, preserveConsent && consentConfirmed);
  };

  const toggleConsent = (event) => {
    const checked = event.target.checked;
    setConsentConfirmed(checked);
    onSelectionChange(participant, checked);
  };

  const run = async (operation) => {
    setBusy(true);
    setError('');
    setMessage('');
    setHandoverLink('');
    setHandoverExpiresAt('');
    try {
      await operation();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'The request could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  const search = () => run(async () => {
    const response = await searchHarvestParticipants(tenantId, query);
    setResults(response.data.participants || []);
  });

  const create = () => run(async () => {
    if (!consentConfirmed) throw new Error('Confirm caller consent before creating a participant profile.');
    const response = await createHarvestParticipant(tenantId, {
      name,
      cardReference,
      email,
      consentConfirmed: true,
      consentMethod: 'phone',
      eligibilityConfirmed,
    });
    setName('');
    setCardReference('');
    setEmail('');
    setEligibilityConfirmed(false);
    selectParticipant(response.data.participant, true);
    setMessage('Participant profile created. Consent and entering-staff attribution will be recorded with the harvest.');
  }).catch((requestError) => {
    if (requestError.response) throw requestError;
    setError(requestError.message);
  });

  const issueInvite = () => run(async () => {
    const response = await issueParticipantInvite(tenantId, participant._id, {
      email,
      consentConfirmed: inviteConsent,
      destinationConfirmed,
    });
    setHandoverLink(`${window.location.origin}/claim-harvest#token=${encodeURIComponent(response.data.token)}`);
    setHandoverExpiresAt(response.data.expiresAt);
    setMessage('Invitation prepared, not sent. Deliver this one-time link directly through the participant-confirmed channel.');
    setInviteConsent(false);
    setDestinationConfirmed(false);
  });

  const issueRecovery = () => run(async () => {
    const response = await issueParticipantRecovery(tenantId, participant._id, {
      participantConfirmed: recoveryConfirmed,
    });
    setHandoverLink(`${window.location.origin}/recover-harvest#token=${encodeURIComponent(response.data.token)}`);
    setHandoverExpiresAt(response.data.expiresAt);
    setMessage('Recovery prepared, not sent. Give the one-time link directly to the participant through the confirmed channel; never ask for or set their password.');
    setRecoveryConfirmed(false);
  });

  return (
    <section aria-label="Assisted participant intake" style={box}>
      <h3 style={{ marginTop: 0 }}>Assisted phone intake</h3>
      <p>Use only for the selected community. Confirm identity and consent with the caller; search exact details before creating a profile. A card reference is optional and is never a login secret.</p>

      {error && <p role="alert" style={{ color: '#991b1b' }}>{error}</p>}
      {message && <p role="status" style={{ color: '#166534' }}>{message}</p>}
      <label style={{ display: 'block', margin: '10px 0' }}>
        <input type="checkbox" checked={consentConfirmed} onChange={toggleConsent} />
        {' '}Caller has agreed to this assisted collection intake and to recording the harvest on their behalf.
      </label>

      <div>
        <h4>Find an existing participant</h4>
        <label>
          Exact name, card reference, or email
          <input style={field} value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
        <button type="button" style={button} disabled={busy || query.trim().length < 2} onClick={search}>Search this community</button>
        {results.length > 0 && (
          <ul aria-label="Participant search results">
            {results.map((entry) => (
              <li key={entry._id} style={{ margin: '6px 0' }}>
                <button type="button" style={button} onClick={() => selectParticipant(entry)}>
                  {entry.name}{entry.cardHint ? ` · card ${entry.cardHint}` : ''}{entry.claimed ? ' · claimed account' : ' · provisional'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <details>
        <summary>Create a provisional participant if no matching profile is found</summary>
        <label>Name<input style={field} maxLength={120} value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label>Card reference (optional; not a password)<input style={field} maxLength={80} value={cardReference} onChange={(event) => setCardReference(event.target.value)} /></label>
        <label>Email, if provided (optional)<input style={field} type="email" maxLength={254} value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label style={{ display: 'block', margin: '10px 0' }}>
          <input type="checkbox" checked={eligibilityConfirmed} onChange={(event) => setEligibilityConfirmed(event.target.checked)} />
          {' '}I have confirmed community membership eligibility for a future claim. Leave unchecked if not established.
        </label>
        <button type="button" style={button} disabled={busy || !consentConfirmed || !name.trim()} onClick={create}>Create participant profile</button>
      </details>

      {participant && (
        <div style={{ marginTop: 14, padding: 12, background: '#f8fafc', borderRadius: 6 }}>
          <strong>Selected participant:</strong> {participant.name}
          {participant.cardHint && <span> · card {participant.cardHint}</span>}
          <button type="button" style={button} onClick={() => selectParticipant(null)}>Clear selection</button>

          <details style={{ marginTop: 10 }}>
            <summary>Prepare profile-claim invitation (requires separate consent)</summary>
            <p>This creates a one-time handover only. CIP has no configured email sender in this flow; the link is not sent automatically.</p>
            <label style={{ display: 'block', margin: '8px 0' }}>
              Participant-confirmed email destination
              <input style={field} type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
            </label>
            <label style={{ display: 'block', margin: '8px 0' }}>
              <input type="checkbox" checked={inviteConsent} onChange={(event) => setInviteConsent(event.target.checked)} />
              {' '}Participant explicitly consented to a profile-claim invitation.
            </label>
            <label style={{ display: 'block', margin: '8px 0' }}>
              <input type="checkbox" checked={destinationConfirmed} onChange={(event) => setDestinationConfirmed(event.target.checked)} />
              {' '}I confirmed the destination with the participant and will deliver the link through that channel.
            </label>
            <button type="button" style={button} disabled={busy || !inviteConsent || !destinationConfirmed || !email.trim()} onClick={issueInvite}>Prepare claim handover</button>
          </details>

          {participant.claimed && (
            <details style={{ marginTop: 10 }}>
              <summary>Prepare account recovery (staff never handles passwords)</summary>
              <label style={{ display: 'block', margin: '8px 0' }}>
                <input type="checkbox" checked={recoveryConfirmed} onChange={(event) => setRecoveryConfirmed(event.target.checked)} />
                {' '}Participant confirmed they requested recovery.
              </label>
              <button type="button" style={button} disabled={busy || !recoveryConfirmed} onClick={issueRecovery}>Prepare recovery handover</button>
            </details>
          )}
        </div>
      )}

      {handoverLink && (
        <div role="group" aria-label="One-time manual handover" style={{ marginTop: 12, padding: 12, border: '1px solid #f59e0b', borderRadius: 6, background: '#fffbeb' }}>
          <strong>One-time manual handover — not sent by email</strong>
          <p>This bearer link is shown once. Deliver it directly through the participant-confirmed channel; do not paste it into audit notes, tickets, or shared records.</p>
          {handoverExpiresAt && <p>Expires: {new Date(handoverExpiresAt).toLocaleString()}</p>}
          <input aria-label="One-time handover link" readOnly value={handoverLink} style={{ ...field, maxWidth: '100%' }} onFocus={(event) => event.target.select()} />
        </div>
      )}
    </section>
  );
}
